import { sharepointService } from './SharePointService';

class InvoiceService {
  private apiBaseUrl = '';
  private customerListName = '/Lists/Customer';

  private parseNumericValue(value: unknown): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.').trim();
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  /**
   * Initialize API URL from environment variable or default
   */
  async initialize() {
    // In development, default to local backend to avoid hitting deployed Azure endpoints by mistake.
    const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (configuredBaseUrl && configuredBaseUrl.trim().length > 0) {
      this.apiBaseUrl = configuredBaseUrl;
    } else {
      this.apiBaseUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:5000'
        : 'https://timemgmtapi-daabf4g6g5endvek.swedencentral-01.azurewebsites.net';
    }
    console.log(`[InvoiceService] Initialized with API URL: ${this.apiBaseUrl}`);
  }


  /**
   * Get all customers from SharePoint
   */
  async getCustomersFromSharePoint(accessToken: string): Promise<Map<string, {id: string}>> {
    try {
      const queryParams = {
        $select: 'Id,Title',
        $top: 1000
      };

      const response = await sharepointService.getListItems(this.customerListName, accessToken, queryParams);

      if (!response.success) {
        console.warn('Could not fetch customers from SharePoint');
        return new Map();
      }

      const customerMap = new Map<string, {id: string}>();
      response.data.forEach((item: any) => {
        if (item.Title) {
          customerMap.set(item.Title, {
            id: item.Id
          });
        }
      });

      return customerMap;
    } catch (error) {
      console.warn('[GetCustomersFromSharePoint] Error:', error);
      return new Map();
    }
  }

  /**
   * Prepare invoice data grouped by customer with activities and projects as separate line items
   * Each unique Activity+Project combination becomes a distinct row with its total hours
   */
  prepareInvoiceData(reportData: any[]): Map<string, {customerName: string; rows: Array<{activity: string; hours: number; project?: string}>}> {
    const invoiceMap = new Map<string, {customerName: string; rows: Array<{activity: string; hours: number; project?: string}>}>();

    reportData.forEach(item => {
      const customer = item.customer;
      const activity = item.activity;
      const hours = item.hours;
      const project = item.project;

      if (!invoiceMap.has(customer)) {
        invoiceMap.set(customer, {
          customerName: customer,
          rows: []
        });
      }

      const invoice = invoiceMap.get(customer)!;
      
      // Create key for unique Activity+Project combination
      const rowKey = project ? `${activity}-${project}` : activity;
      
      // Check if this activity+project combination already exists for this customer
      const existingRow = invoice.rows.find(row => {
        const existingKey = row.project ? `${row.activity}-${row.project}` : row.activity;
        return existingKey === rowKey;
      });
      
      if (existingRow) {
        existingRow.hours += hours;
      } else {
        invoice.rows.push({
          activity,
          hours,
          ...(project && { project })
        });
      }
    });

    return invoiceMap;
  }

  /**
   * Create invoice via backend API (safe - credentials on backend)
   * Backend handles customer/product creation and invoice creation
   */
  private async fetchOurReference(customerName: string, accessToken: string): Promise<string | undefined> {
    try {
      const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
      const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
      const encodedCustomer = encodeURIComponent(`'${customerName}'`);
      const response = await fetch(
        `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Customer')/items?$filter=Title eq ${encodedCustomer}&$select=Ansvarig/Title&$expand=Ansvarig&$top=1`,
        { headers: { 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' } }
      );
      if (!response.ok) return undefined;
      const data = await response.json();
      return data?.value?.[0]?.Ansvarig?.Title || undefined;
    } catch {
      return undefined;
    }
  }

  private async fetchYourReference(customerName: string, accessToken: string): Promise<string | undefined> {
    try {
      const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
      const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
      const encodedCustomer = encodeURIComponent(`'${customerName}'`);
      const response = await fetch(
        `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Faktureringsadresser')/items?$filter=Customer/Title eq ${encodedCustomer}&$select=M_x00e4_rkning,Customer/Title&$expand=Customer&$top=1`,
        { headers: { 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' } }
      );
      if (!response.ok) return undefined;
      const data = await response.json();
      return data?.value?.[0]?.M_x00e4_rkning || undefined;
    } catch {
      return undefined;
    }
  }

  async createInvoice(
    customerName: string,
    rows: Array<{activity: string; hours: number; project?: string; unitPrice?: number; quantity?: number; text?: string; pricingActivity?: string}>,
    accessToken: string,
    yourReference?: string,
    ourReference?: string
  ): Promise<any> {
    try {
      if (!customerName) {
        throw new Error('Kundnamn saknas');
      }

      if (!rows || rows.length === 0) {
        throw new Error('Inga aktiviteter/rader för fakturan');
      }

      // Call backend API which handles everything securely
      const response = await fetch(`${this.apiBaseUrl}/api/createInvoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerName,
          rows,
          accessToken,
          yourReference,
          ourReference
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(`Kunde inte skapa faktura: ${errorData.error || JSON.stringify(errorData)}`);
        } catch (parseError) {
          throw new Error(`Kunde inte skapa faktura: ${response.status} - ${errorText}`);
        }
      }

      const responseText = await response.text();
      console.log('Backend response text:', responseText);
      
      if (!responseText) {
        // Empty response is okay - assume success
        return { success: true, message: 'Faktura skapad' };
      }

      try {
        const result = JSON.parse(responseText);
        console.log('Parsed response:', result);
        return result;
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        // Try to handle Azure Functions response format
        return { success: true, message: 'Faktura skapad' };
      }
    } catch (error) {
      console.error('[CreateInvoice]', error);
      throw error;
    }
  }

  /**
   * Export filtered report data to Fakturan.nu invoices
   * Creates rows for each unique Activity+Project combination
   */
  async exportToInvoices(
    reportData: any[],
    selectedCustomer: string,
    accessToken: string,
    options?: {
      groupingMode?: 'project' | 'activity';
      projectMonthPrices?: Record<number, { title: string; monthPrice: number }>;
      activityPrices?: Record<string, number>;
    }
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    try {
      // If no customer selected, we can't export
      if (!selectedCustomer || selectedCustomer === 'all') {
        throw new Error('Du måste välja en kund för att exportera till faktura');
      }

      const groupingMode = options?.groupingMode || 'project';
      const projectMonthPrices = options?.projectMonthPrices || {};
      const activityPrices = options?.activityPrices || {};

      // Normalize to time entry objects regardless of input shape.
      const allEntries: any[] = [];
      reportData.forEach(item => {
        if (item?.entries && Array.isArray(item.entries)) {
          allEntries.push(...item.entries);
          return;
        }
        if (item?.Customer?.Title && item?.Activity?.Title) {
          allEntries.push(item);
        }
      });

      const customerEntries = allEntries.filter((entry: any) => entry.Customer?.Title === selectedCustomer);

      if (customerEntries.length === 0) {
        throw new Error(`Inga aktiviteter för kund: ${selectedCustomer}`);
      }

      const rows: Array<{activity: string; hours: number; project?: string; unitPrice?: number; quantity?: number; text?: string; pricingActivity?: string}> = [];
      const handledMonthPriceProjects = new Set<number>();

      // Add month-price projects as single fixed-price rows and skip their hours.
      customerEntries.forEach((entry: any) => {
        const projectId = entry.Project?.Id;
        if (typeof projectId !== 'number') {
          return;
        }

        const monthPriceInfo = projectMonthPrices[projectId];
        if (!monthPriceInfo || this.parseNumericValue(monthPriceInfo.monthPrice) <= 0) {
          return;
        }

        if (handledMonthPriceProjects.has(projectId)) {
          return;
        }

        handledMonthPriceProjects.add(projectId);
        const projectTitle = monthPriceInfo.title || entry.Project?.Title || `ID ${projectId}`;
        rows.push({
          activity: projectTitle,
          project: projectTitle,
          hours: 1,
          quantity: 1,
          unitPrice: this.parseNumericValue(monthPriceInfo.monthPrice)
        });
      });

      const billableTimeEntries = customerEntries.filter((entry: any) => {
        const projectId = entry.Project?.Id;
        if (typeof projectId !== 'number') {
          return true;
        }
        const monthPriceInfo = projectMonthPrices[projectId];
        return !monthPriceInfo || this.parseNumericValue(monthPriceInfo.monthPrice) <= 0;
      });

      if (groupingMode === 'activity') {
        const activityMap = new Map<string, { hours: number; pricingActivity: string; activity: string; text?: string }>();

        billableTimeEntries.forEach((entry: any) => {
          const activityName = entry.Activity?.Title || 'Unknown';
          const projectName = entry.Project?.Title;
          const rowKey = projectName ? `${projectName}||${activityName}` : activityName;
          const hours = this.parseNumericValue(entry.Hours);

          if (!activityMap.has(rowKey)) {
            activityMap.set(rowKey, {
              hours: 0,
              pricingActivity: activityName,
              activity: projectName || activityName,
              ...(projectName && { text: activityName })
            });
          }

          const current = activityMap.get(rowKey)!;
          current.hours += hours;
        });

        const sortedActivityRows = Array.from(activityMap.values()).sort((a, b) => {
          const projectCompare = a.activity.localeCompare(b.activity, 'sv-SE');
          if (projectCompare !== 0) {
            return projectCompare;
          }
          return (a.text || '').localeCompare(b.text || '', 'sv-SE');
        });

        sortedActivityRows.forEach((values) => {
          rows.push({
            activity: values.activity,
            hours: values.hours,
            pricingActivity: values.pricingActivity,
            ...(values.text && { text: values.text })
          });
        });
      } else {
        // Group by project and send a single fixed-price row per project.
        const projectMap = new Map<string, { amount: number; totalHours: number }>();

        billableTimeEntries.forEach((entry: any) => {
          const projectName = entry.Project?.Title || 'Övrigt';
          const activityName = entry.Activity?.Title || 'Unknown';
          const hours = this.parseNumericValue(entry.Hours);
          const rate = this.parseNumericValue(activityPrices[`${activityName}|${selectedCustomer}`]);
          const amount = hours * rate;

          if (!projectMap.has(projectName)) {
            projectMap.set(projectName, { amount: 0, totalHours: 0 });
          }

          const row = projectMap.get(projectName)!;
          row.amount += amount;
          row.totalHours += hours;
        });

        projectMap.forEach((values, projectName) => {
          if (values.amount <= 0 || values.totalHours <= 0) {
            return;
          }
          const avgRate = values.amount / values.totalHours;
          rows.push({
            activity: projectName,
            project: projectName,
            hours: values.totalHours,
            quantity: values.totalHours,
            unitPrice: avgRate
          });
        });
      }

      if (rows.length === 0) {
        throw new Error('Inga tidsregistreringar att exportera för vald kund');
      }

      const yourReference = await this.fetchYourReference(selectedCustomer, accessToken);
      const ourReference = await this.fetchOurReference(selectedCustomer, accessToken);

      const results = { success: 0, failed: 0, errors: [] as string[] };

      try {
        const response = await this.createInvoice(selectedCustomer, rows, accessToken, yourReference, ourReference);

        if (response && (response.invoiceId || response.data?.id)) {
          results.success++;
          console.log(`Successfully created invoice for ${selectedCustomer}`);
        } else {
          results.failed++;
          results.errors.push(`Kunde inte skapa faktura för ${selectedCustomer}`);
        }
      } catch (error) {
        results.failed++;
        results.errors.push(
          `Fel för ${selectedCustomer}: ${error instanceof Error ? error.message : 'Okänt fel'}`
        );
      }

      return results;
    } catch (error) {
      console.error('[ExportToInvoices]', error);
      throw error;
    }
  }
}

export const invoiceService = new InvoiceService();
